<template>
  <div id="loginPage">
    <p class="control">
      <button @click.prevent="socialLogin('facebook', 'FB')" class="button is-fullwidth hp-btn-primary social-button fb-button"><i
        class="fab fa-facebook-f"></i>&nbsp;SIGN IN WITH FACEBOOK
      </button>
    </p>
    <button @click.prevent="socialLogin('twitter', 'TW')" class="button is-fullwidth hp-btn-primary social-button x-button">
      <img src="/static/img/social/twitter_black.svg"/>&nbsp;SIGN IN WITH TWITTER
    </button>
    <form novalidate v-on:submit.prevent="login">
      <p class="control divider line one-line has-text-weight-bold">OR</p>
      <flash-message size="is-size-7"></flash-message>
      <div v-show="show_custom_flash">
        <div class="columns is-mobile is-marginless" v-if="show_custom_flash">
          <div class="column is-narrow is-marginless is-centered is-danger">
                            <span class="icon is-size-4 is-white">
                              <i class="fas fa-ban"></i>
                            </span>
          </div>
          <div class="column is-marginless is-danger-middle">
            <p class="has-text-weight-bold is-size-7">
              Your activation token has expired. Please <a @click="resendActivationToken()">click here</a>
              to send a new account activation email.
            </p>
          </div>
          <div class="column is-narrow is-marginless is-danger-middle">
            <a class="icon is-size-4" href="#" @click.prevent="closeCustomFlash">
              <i class="far fa-times-circle"></i>
            </a>
          </div>
        </div>
      </div>

      <p class="control">
        <input tabindex="1" :disabled="sending" class="input hp-input" type="email" name="email"
               placeholder="Email address or username" v-model="form.email"/>
      </p>
      <p class="control has-icons-right">
        <vue-password tabindex="2" :disabled="sending" v-model="form.password" classes="input hp-input"
                      :disableStrength="disableStrength" placeholder="Password"></vue-password>
      </p>
      <p class="control has-text-right is-size-7">
        <a href="forgot-password" class="link--v2">Forgot password?</a>
      </p>

      <p class="control">
        <button
          tabindex="3"
          :disabled="canSubmit"
          @click.prevent="login"
          type="submit"
          :class="[{ 'is-loading': sending}, 'hp-btn-primary', 'is-fullwidth', 'button']">SIGN IN
        </button>
      </p>
      <p class="control has-text-centered has-text-weight-bold is-size-7">
        New to Hellopeter.com?<br/>
        <router-link to="/register" class="has-text-weight-normal link--v2">Create an account now
        </router-link>
      </p>
    </form>
  </div>
</template>
<script>
/* eslint-disable indent,no-new,no-undef */
import axios from 'axios'
import auth from '@/mixins/auth.js'
import ApiResource from '@/api/ApiResource'
import VuePassword from 'vue-password'
import FlashMessage from '@/components/FlashMessage'

export default {
  metaInfo () {
    return {
      title: 'Login - Review Companies Online | Customer Service & Company Ratings | hellopeter.com'
    }
  },
  components: {
    VuePassword,
    FlashMessage
  },
  mixins: [auth],
  data () {
    return {
      disableStrength: true,
      sending: false,
      form: new ApiResource({
        email: '',
        password: ''
      }),
      error: false,
      socialError: false,
      error_message: '',
      has_notification: false,
      show_custom_flash: false,
      uuid: null
    }
  },
  mounted () {
    this.$gtm.trackEvent({
      event: 'pageLoad',
      virtualPageUrl: this.$router.currentRoute.path,
      virtualPageTitle: 'Login'
    })
    this.has_notification = typeof this.$route.query.notification !== 'undefined' && this.$route.query.notification.length > 0

    if (this.$route.query.flash && ('success' in this.$route.query) && this.$route.query.success === 'true') {
      let datas = this.$route.query
      flash(this.$route.query.flash, 'is-info')
    } else if ('uuid' in this.$route.query && this.$route.query.uuid !== '') {
      this.uuid = this.$route.query.uuid
      this.show_custom_flash = true
    } else if (this.$route.query.flash) {
      flash(this.$route.query.flash, 'is-danger')
    }
  },
  watch: {
    has_errors () {
      if (this.form.errors.any()) {
        flash(this.getErrorMessage(), 'is-danger')
      }
    },
    has_notification () {
      if (this.has_notification) {
        const header = this.error ? 'is-danger' : 'is-success'
        flash(this.$route.query.notification, header, true)
      }
    }
  },
  created () {
    this.error = false
    this.socialError = false
    this.error_message = auth.user.error_message
    auth.redirectSocial = loggedIn => {
      this.socialError = auth.user.socialError
      this.error_message = auth.user.error_message
      auth.user.authenticated = loggedIn
      if (loggedIn) {
        delete window.auth
        this.redirect()
      }
    }
    auth.showError = (message, errorObject = {}) => {
      this.error = true
      this.error_message = message
      flash(message, 'is-danger', true)
    }

    auth.showRequireEmail = (requiresEmail) => {
      this.error = true
      this.error_message = `There is no account with these credentials or the account is unverified. Please <a href="/register"><span class="text-white font-weight-bold">create or complete account to continue</span></a>`
      flash(this.error_message, 'is-danger', true)
    }

    auth.verifyPhone = (shouldRedirect, uuid) => {
      if (shouldRedirect) {
        this.$router.push(`/verify-phone/${uuid}`);
      }
    }

    window.auth = auth

    if (auth.isLoggedIn()) {
      this.redirect()
    }

    window.prerenderReady = true
  },
  computed: {
    canSubmit () {
      const username = this.form.email
      const password = this.form.password

      return username.length === 0 || password.length === 0
    },
    redirectUrl () {
      if (this.$route.query.redirect) {
        return this.$route.query.redirect
      }
      return '/'
    }
  },
  methods: {
    handleClick () {
      this.$emit('click')
    },
    redirect () {
      this.$router.replace(this.redirectUrl)
      window.prerenderReady = true
    },
    login () {
      this.form.errors.clear(null)
      this.sending = true
      this.form.send('/consumer/login')
        .then(() => {
          this.sending = false
          if (typeof this.form.token !== 'undefined' && this.form.token.length > 0) {
            localStorage.setItem('auth_token', this.form.token)
            localStorage.setItem('profile_id', this.form.profile_id)
            localStorage.setItem('mobile', this.form.mobile)
            localStorage.setItem('displayName', this.form.displayName)
            localStorage.setItem('avatar', this.form.avatar)
            this.$gtm.trackEvent({
              event: 'conversionSuccess',
              type: 'Login'
            })
            this.$router.replace(this.redirectUrl)
          } else {
            if (typeof this.form.profile_id !== 'undefined' && this.form.profile_id.length > 0) {
              this.$router.push(`/verify-phone/${this.form.profile_id}`)
            } else {
              msg = 'Your login credentials are correct, but the system could not proceed. Please contact admin@hellopeter.com'
              flash(msg, 'is-danger')
            }
          }
        })
        .catch((error) => {
          this.sending = false
          if (typeof error !== 'undefined' && typeof error.uuid !== 'undefined' && error.uuid.length > 0) {
            this.$router.push(`/verify-phone/${error.uuid}`)
          } else {
            let msg = this.form.errors.getDefaultErrorMessage()
            const errorStatus = this.form.errors.getStatus()
            if (errorStatus !== null && errorStatus === 403) {
              msg = 'Your account is inactive. Please check your email for an activation link or contact us to activate your account.'
            }
            flash(msg, 'is-danger')
          }
        })
    },
    socialLogin (provider, alias) {
      let socialAlias = 'loading' + alias
      let windowReference = window.open('about:blank', `${provider}Login`, 'width=585,height=550,toolbar=0,menubar=0,location=0,status=0,scrollbars=1,resizable=1,left=400,top=100')
      this[socialAlias] = true
      axios.get(`/consumer/social-login/${provider}`)
        .then((response) => {
          windowReference.location = response.data.url
          this[socialAlias] = false
        })
        .catch((error) => {
          windowReference.close()
        })
    },
    resendActivationToken () {
      axios.get(`/consumer/user/${this.uuid}/resend-activation-token`)
        .then((response) => {
          this.show_custom_flash = false
          flash(response.data.message, 'is-success')
        })
        .catch((error) => {
          this.show_custom_flash = false
          flash(error.response.data.message, 'is-danger')
        })
    },
    /**
     * User clicked on the custom close button
     * @returns {void}
     */
    closeCustomFlash () {
      this.show_custom_flash = false
    }
  }
}
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/vendor/_breakpoints.scss";

.x-button {
  background-color: #000;

  img {
    width: 32px;
  }
}

.is-danger-middle {
  background-color: #fbcdc6;
}

.is-danger {
  color: #fff;
  background-color: #fb492c;
}

a.icon {
  text-decoration: none;
  color: #58595b;
}

#loginPage .control:last-child {
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/pages/auth/Login.vue